import { useEffect, useMemo, useState } from "react";
import { markeerTaalGekozen } from "../TaalVanAccount";
import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useSetLocale,
  useTranslate,
} from "@refinedev/core";
import { User as UserIcon } from "lucide-react";
import { Button, Field, Modal, SelectField, Tabs, type SelectOption } from "@fawkes/ui";

import {
  applyEigenThema,
  applyTheme,
  clearEigenThema,
  EIGEN_THEMA,
  persistEigenThema,
  persistTheme,
  storedEigenPalet,
  storedTheme,
  themeNames,
  type EigenPalet,
  type ThemeName,
} from "../theme";
import {
  meldFotoGewijzigd,
  profielfotoUrl,
  uploadProfielfoto,
  verwijderProfielfoto,
} from "../api/profielfoto";
import { MijnApparatenTab } from "./voorkeuren/MijnApparatenTab";

type Tab = "profiel" | "vestiging" | "thema" | "taal" | "notificaties" | "filters" | "apparaten";

interface Me {
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  canChangePassword: boolean;
}

interface Vestiging {
  id: number;
  naam: string;
  plaats?: string | null;
}

/**
 * "Mijn voorkeuren" — 1:1 met acc (maintainer 2026-08-18: "kijk ook even naar de schermen bij de
 * gebruiker neem die van de acc over"). Een dialoog over het scherm waar je stond, met zes tabs:
 * Profiel · Vestiging · Thema · Taal · Notificaties · Standaard filters, en onderaan Annuleren /
 * Opslaan.
 *
 * <p><b>Eén Opslaan voor de hele dialoog.</b> Elke tab houdt zijn eigen wijziging vast; pas Opslaan
 * schrijft ze weg. Het thema is de uitzondering die acc ook maakt: klikken toont hem meteen als live
 * preview, en Annuleren zet het thema terug dat je bij openen had — anders zou je een thema moeten
 * kiezen zonder te zien wat het doet.
 */
export function Voorkeuren({ onClose }: { onClose: () => void }) {
  const t = useTranslate();
  const [tab, setTab] = useState<Tab>("profiel");

  // ── profiel ──────────────────────────────────────────────────────────────────────────────────
  const { data: meFeed, isLoading: meBezig, isError: meFout } = useCustom<Me>({
    url: "auth/me", method: "get",
  });
  const me = meFeed?.data;
  const [voornaam, setVoornaam] = useState("");
  const [achternaam, setAchternaam] = useState("");
  const [email, setEmail] = useState("");
  const [gevuld, setGevuld] = useState(false);
  // De profielfoto: de bytes komen achter de bearer vandaan, dus als object-URL.
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [fotoBezig, setFotoBezig] = useState(false);
  const [fotoFout, setFotoFout] = useState<string | null>(null);
  useEffect(() => {
    let levend = true;
    void profielfotoUrl().then((url) => {
      if (levend) {
        setFotoUrl(url);
      } else if (url) {
        URL.revokeObjectURL(url);
      }
    });
    return () => { levend = false; };
  }, []);
  useEffect(() => {
    if (me && !gevuld) {
      setVoornaam(me.firstName ?? "");
      setAchternaam(me.lastName ?? "");
      setEmail(me.email ?? "");
      setGevuld(true);
    }
  }, [me, gevuld]);
  // Een extern beheerd account (HelloID/OIDC) heeft zijn naam en wachtwoord daar staan; hier lezen.
  const externBeheerd = me != null && !me.canChangePassword;

  // ── vestiging ────────────────────────────────────────────────────────────────────────────────
  const { data: vestigingenFeed } = useCustom<Vestiging[]>({ url: "vestigingen", method: "get" });
  const { data: huidigeFeed } = useCustom<Vestiging>({ url: "vestigingen/current", method: "get" });
  const vestigingen = useMemo(() => {
    const rijen = vestigingenFeed?.data;
    return Array.isArray(rijen) ? rijen : [];
  }, [vestigingenFeed]);
  const [vestigingId, setVestigingId] = useState<string>("");
  useEffect(() => {
    const huidig = huidigeFeed?.data?.id;
    if (huidig != null && vestigingId === "") {
      setVestigingId(String(huidig));
    }
  }, [huidigeFeed, vestigingId]);

  // ── thema ────────────────────────────────────────────────────────────────────────────────────
  // Het thema van vóór de dialoog: Annuleren zet de live preview daarmee terug.
  const [themaBijOpenen] = useState<ThemeName>(() => storedTheme());
  const [eigenBijOpenen] = useState<EigenPalet | null>(() => storedEigenPalet());
  const [warenEigen] = useState(
    () => globalThis.localStorage?.getItem("fawkes.theme") === EIGEN_THEMA && eigenBijOpenen != null,
  );
  const [thema, setThema] = useState<ThemeName | typeof EIGEN_THEMA>(
    warenEigen ? EIGEN_THEMA : themaBijOpenen,
  );
  // Het eigen thema: het afgeleide palet, het adres waar het vandaan komt, en wat de site opleverde.
  const [eigenPalet, setEigenPalet] = useState<EigenPalet | null>(eigenBijOpenen);
  const [website, setWebsite] = useState(eigenBijOpenen?.bron ?? "");
  const [themaBezig, setThemaBezig] = useState(false);
  const [themaFout, setThemaFout] = useState<string | null>(null);
  const [gevonden, setGevonden] = useState<string[]>([]);
  const { mutate: genereerThema } = useCustomMutation();

  // ── taal ─────────────────────────────────────────────────────────────────────────────────────
  const setLocale = useSetLocale();
  const getLocale = useGetLocale();
  const taalBijOpenen = getLocale() ?? "nl";
  const [taal, setTaal] = useState(taalBijOpenen);

  const { mutate: bewaar, isLoading: bezig } = useCustomMutation();
  const { mutate: vraagWachtwoord } = useCustomMutation();
  const [fout, setFout] = useState<string | null>(null);
  const [wachtwoordBericht, setWachtwoordBericht] = useState<string | null>(null);

  const tabs = [
    { key: "profiel", label: t("voorkeuren.tab.profiel") },
    { key: "vestiging", label: t("voorkeuren.tab.vestiging") },
    { key: "thema", label: t("voorkeuren.tab.thema") },
    { key: "taal", label: t("voorkeuren.tab.taal") },
    { key: "notificaties", label: t("voorkeuren.tab.notificaties") },
    { key: "filters", label: t("voorkeuren.tab.filters") },
    // Achteraan: de tab is nieuw, en de volgorde die iedereen gewend is blijft zo intact.
    { key: "apparaten", label: t("voorkeuren.tab.apparaten") },
  ];

  function annuleer() {
    // De live preview van het thema is een voorbeeld, geen keuze: bij annuleren gaat hij terug.
    if (warenEigen && eigenBijOpenen) {
      applyEigenThema(eigenBijOpenen);
    } else {
      clearEigenThema();
      applyTheme(themaBijOpenen);
    }
    onClose();
  }

  function opslaan() {
    setFout(null);
    if (thema === EIGEN_THEMA && eigenPalet) {
      persistEigenThema(eigenPalet);
    } else {
      clearEigenThema();
      persistTheme(thema as ThemeName);
    }

    const klaar = () => {
      // De taal die hier staat is een keuze, ook als hij gelijk is aan wat het systeem al gaf (Maarten,
      // 2026-10-13: "Gebruik deze eens!"). Opslaan legt hem vast: in deze browser, en bij het account, zodat hij
      // op elk apparaat vóór de systeemtaal gaat.
      void setLocale(taal);
      if (taal !== taalBijOpenen) {
        // De hele app hertaalt zich bij het laden van de bundel; herladen is de eerlijkste manier.
        markeerTaalGekozen(); // na het herladen gaat deze keuze naar het account
        globalThis.location?.reload();
        return;
      }
      bewaar(
        { url: "auth/me/taal", method: "put", values: { taal }, successNotification: false, errorNotification: false },
        // Zonder beheeraccount kan het niet bij het account; de keuze geldt dan in deze browser.
        { onSuccess: () => onClose(), onError: () => onClose() },
      );
    };

    const vestigingWijzigt =
      vestigingId !== "" && Number(vestigingId) !== (huidigeFeed?.data?.id ?? null);

    const naVestiging = () => (vestigingWijzigt
      ? bewaar(
          { url: "vestigingen/current", method: "put", values: { id: Number(vestigingId) } },
          { onSuccess: () => klaar(), onError: () => setFout(t("voorkeuren.opslaanFout")) },
        )
      : klaar());

    if (externBeheerd || me == null) {
      naVestiging();
      return;
    }
    bewaar(
      {
        url: "auth/me",
        method: "patch",
        values: { firstName: voornaam, lastName: achternaam, email },
      },
      { onSuccess: () => naVestiging(), onError: () => setFout(t("voorkeuren.opslaanFout")) },
    );
  }

  function wachtwoordWijzigen() {
    setWachtwoordBericht(null);
    vraagWachtwoord(
      { url: "auth/password-change-request", method: "post", values: {} },
      {
        onSuccess: (res) => {
          const uitkomst = (res.data as { outcome?: string })?.outcome;
          setWachtwoordBericht(
            uitkomst === "SENT" ? t("voorkeuren.password.sent")
              : uitkomst === "NO_EMAIL_ON_FILE" ? t("voorkeuren.password.noEmail")
                : uitkomst === "OIDC_NOT_ALLOWED" ? t("voorkeuren.password.oidcDisabled")
                  : t("voorkeuren.password.failed"),
          );
        },
        onError: () => setWachtwoordBericht(t("voorkeuren.password.failed")),
      },
    );
  }

  return (
    <Modal
      open
      title={t("voorkeuren.title")}
      onClose={annuleer}
      size="wide"
      footer={
        <>
          <button type="button" className="fawkes-fin__link" onClick={annuleer}>
            {t("common.cancel")}
          </button>
          <Button variant="primary" onClick={opslaan} disabled={bezig}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="voorkeuren">
        <Tabs
          tabs={tabs}
          activeKey={tab}
          onChange={(k) => setTab(k as Tab)}
          ariaLabel={t("voorkeuren.title")}
        />

        {tab === "profiel" && (
          <div className="voorkeuren__paneel">
            {meBezig && <p className="fawkes-muted">{t("common.loading")}</p>}
            {meFout && <p className="fawkes-error">{t("voorkeuren.profiel.loadFailed")}</p>}
            {me && (
              <>
                <div className="voorkeuren__foto">
                  <span className="voorkeuren__avatar" aria-hidden="true">
                    {fotoUrl
                      ? <img src={fotoUrl} alt="" />
                      : <UserIcon size={28} />}
                  </span>
                  <div>
                    <strong>{t("voorkeuren.profiel.foto")}</strong>
                    <div className="voorkeuren__foto-acties">
                      <label className="fawkes-fin__link voorkeuren__fotoknop">
                        {t(fotoBezig ? "common.loading" : "voorkeuren.profiel.fotoKiezen")}
                        <input
                          type="file" accept="image/jpeg,image/png"
                          onChange={(e) => {
                            const bestand = e.target.files?.[0];
                            e.target.value = "";
                            if (!bestand) return;
                            setFotoFout(null);
                            setFotoBezig(true);
                            uploadProfielfoto(bestand)
                              .then(() => profielfotoUrl())
                              .then((url) => {
                                setFotoUrl((oud) => { if (oud) URL.revokeObjectURL(oud); return url; });
                                meldFotoGewijzigd();
                              })
                              .catch(() => setFotoFout(t("voorkeuren.profiel.fotoFout")))
                              .finally(() => setFotoBezig(false));
                          }}
                        />
                      </label>
                      {fotoUrl && (
                        <button
                          type="button" className="fawkes-fin__link"
                          onClick={() => {
                            setFotoFout(null);
                            verwijderProfielfoto()
                              .then(() => {
                                setFotoUrl((oud) => { if (oud) URL.revokeObjectURL(oud); return null; });
                                meldFotoGewijzigd();
                              })
                              .catch(() => setFotoFout(t("voorkeuren.profiel.fotoFout")));
                          }}
                        >
                          {t("voorkeuren.profiel.fotoVerwijderen")}
                        </button>
                      )}
                    </div>
                    <p className="voorkeuren__hint">{t("voorkeuren.profiel.fotoHint")}</p>
                    {fotoFout && (
                      <p className="fawkes-inboeken__field-error" role="alert">{fotoFout}</p>
                    )}
                  </div>
                </div>

                <div className="voorkeuren__rij">
                  <Field
                    label={t("voorkeuren.profiel.firstName")} value={voornaam}
                    readOnly={externBeheerd} onChange={(e) => setVoornaam(e.target.value)}
                  />
                  <Field
                    label={t("voorkeuren.profiel.lastName")} value={achternaam}
                    readOnly={externBeheerd} onChange={(e) => setAchternaam(e.target.value)}
                  />
                </div>
                <Field
                  label={t("voorkeuren.profiel.email")} type="email" value={email}
                  invalidEmailMessage={t("validatie.emailadres")}
                  readOnly={externBeheerd} onChange={(e) => setEmail(e.target.value)}
                />
                <p className="voorkeuren__hint">
                  {t(externBeheerd
                    ? "voorkeuren.profiel.managedExternally"
                    : "voorkeuren.profiel.lokaal")}
                </p>

                {me.canChangePassword && (
                  <div className="voorkeuren__wachtwoord">
                    <Button variant="secondary" onClick={wachtwoordWijzigen}>
                      {t("voorkeuren.password.button")}
                    </Button>
                    <p className="voorkeuren__hint">{t("voorkeuren.password.hint")}</p>
                    {wachtwoordBericht && (
                      <p className="fawkes-muted" role="status">{wachtwoordBericht}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {tab === "vestiging" && (
          <div className="voorkeuren__paneel">
            <p className="voorkeuren__hint">{t("voorkeuren.vestiging.uitleg")}</p>
            <SelectField
              label={t("voorkeuren.vestiging.label")}
              options={vestigingen.map((v) => ({
                value: String(v.id),
                label: v.plaats ? `${v.naam} ${v.plaats}` : v.naam,
              }))}
              value={vestigingId}
              onChange={(e) => setVestigingId(e.target.value)}
            />
          </div>
        )}

        {tab === "thema" && (
          <div className="voorkeuren__paneel">
            <p className="voorkeuren__hint">{t("voorkeuren.thema.uitleg")}</p>
            <div className="voorkeuren__themas">
              {themeNames.map((naam) => (
                <button
                  key={naam}
                  type="button"
                  className={naam === thema
                    ? "voorkeuren__thema is-gekozen" : "voorkeuren__thema"}
                  aria-pressed={naam === thema}
                  onClick={() => {
                    setThema(naam);
                    clearEigenThema();
                    applyTheme(naam); // live preview; Opslaan bevestigt, Annuleren draait terug
                  }}
                >
                  <strong>{t(`voorkeuren.theme.${naam}`, undefined, naam)}</strong>
                  <span className="voorkeuren__thema-uitleg">
                    {t(`voorkeuren.theme.${naam}.uitleg`, undefined, "")}
                  </span>
                  <span className={`voorkeuren__staal voorkeuren__staal--${naam}`} aria-hidden="true">
                    <i /><i /><i /><i />
                  </span>
                </button>
              ))}

              {/* Het eigen thema: pas een kaart om te kiezen zodra er een palet is — daarvóór is er
                  niets om te kiezen, alleen iets om te genereren. */}
              <button
                type="button"
                className={thema === EIGEN_THEMA
                  ? "voorkeuren__thema is-gekozen" : "voorkeuren__thema"}
                aria-pressed={thema === EIGEN_THEMA}
                disabled={eigenPalet == null}
                onClick={() => {
                  if (!eigenPalet) return;
                  setThema(EIGEN_THEMA);
                  applyEigenThema(eigenPalet);
                }}
              >
                <strong>{t("voorkeuren.theme.eigen")}</strong>
                <span className="voorkeuren__thema-uitleg">{t("voorkeuren.theme.eigen.uitleg")}</span>
                <span className="voorkeuren__staal" aria-hidden="true">
                  {eigenPalet ? (
                    <>
                      <i style={{ background: eigenPalet.primair }} />
                      <i style={{ background: eigenPalet.oppervlak }} />
                      <i style={{ background: eigenPalet.accent }} />
                      <i style={{ background: eigenPalet.achtergrond }} />
                    </>
                  ) : (
                    <><i /><i /><i /><i /></>
                  )}
                </span>
              </button>
            </div>

            {/* Waar het eigen thema vandaan komt: een adres, en wat de site aan kleuren opleverde. */}
            <div className="voorkeuren__eigen">
              <p className="voorkeuren__hint">{t("voorkeuren.thema.eigenUitleg")}</p>
              <div className="voorkeuren__eigen-rij">
                <Field
                  label={t("voorkeuren.thema.website")}
                  value={website}
                  placeholder="https://example.com"
                  onChange={(e) => { setWebsite(e.target.value); setThemaFout(null); }}
                />
                <Button
                  variant="secondary"
                  disabled={themaBezig || website.trim() === ""}
                  onClick={() => {
                    setThemaFout(null);
                    setThemaBezig(true);
                    genereerThema(
                      { url: "thema/uit-website", method: "post", values: { url: website.trim() } },
                      {
                        onSuccess: (res) => {
                          const p = res?.data as (EigenPalet & { gevonden?: string[] }) | undefined;
                          if (!p?.primair) {
                            setThemaFout(t("voorkeuren.thema.eigenFout"));
                            return;
                          }
                          setEigenPalet(p);
                          setGevonden(p.gevonden ?? []);
                          setThema(EIGEN_THEMA);
                          applyEigenThema(p); // meteen zien wat eruit kwam
                        },
                        // De api zegt precies wát er mis is (adres bestaat niet, pagina weigerde,
                        // geen kleuren gevonden); dat is bruikbaarder dan onze eigen algemene zin.
                        onError: (fout) => {
                          const bericht = (fout as { message?: string } | undefined)?.message?.trim();
                          setThemaFout(bericht && bericht.length < 200
                            ? bericht
                            : t("voorkeuren.thema.eigenFout"));
                        },
                        onSettled: () => setThemaBezig(false),
                      },
                    );
                  }}
                >
                  {t(themaBezig ? "voorkeuren.thema.bezig" : "voorkeuren.thema.genereer")}
                </Button>
              </div>
              {gevonden.length > 0 && (
                <p className="voorkeuren__gevonden">
                  <span className="voorkeuren__hint">{t("voorkeuren.thema.gevonden")}</span>
                  {gevonden.map((kleur) => (
                    <i key={kleur} style={{ background: kleur }} title={kleur} />
                  ))}
                </p>
              )}
              {themaFout && (
                <p className="fawkes-inboeken__field-error" role="alert">{themaFout}</p>
              )}
            </div>
          </div>
        )}

        {tab === "taal" && (
          <div className="voorkeuren__paneel">
            <p className="voorkeuren__hint">{t("voorkeuren.taal.uitleg")}</p>
            <SelectField
              label={t("voorkeuren.taal.label")}
              options={[
                { value: "nl", label: "Nederlands" },
                { value: "en", label: "English" },
              ] as SelectOption[]}
              value={taal}
              onChange={(e) => setTaal(e.target.value)}
            />
          </div>
        )}

        {tab === "notificaties" && (
          <div className="voorkeuren__paneel">
            <p className="voorkeuren__hint">{t("voorkeuren.notificaties.uitleg")}</p>
            <p className="voorkeuren__leeg">{t("voorkeuren.notificaties.leeg")}</p>
          </div>
        )}

        {tab === "filters" && (
          <div className="voorkeuren__paneel">
            <p className="voorkeuren__hint">{t("voorkeuren.filters.uitleg")}</p>
          </div>
        )}

        {/* Deze tab valt buiten de "één Opslaan"-regel hierboven: koppelen en intrekken zijn
            handelingen die meteen gebeuren, niet een veld dat je invult. Een QR die pas na Opslaan
            geldig wordt zou onzin zijn, en Annuleren kan een ingetrokken toestel niet terughalen. */}
        {tab === "apparaten" && <MijnApparatenTab />}

        {fout && <p className="fawkes-inboeken__field-error" role="alert">{fout}</p>}
      </div>
    </Modal>
  );
}
